// Working out the dashboard rows' attention sets: the work that reads their
// inputs and overrides and runs the engine's rules, answering with what it
// learned as data (`AttentionResult`), and the pure step that folds that
// into what the dashboard knows (`applyAttention`).
//
// Kept inputs are checked first, with one cheap request for the rows a run
// hands over close together (`CheckQueue`); only the rest are read in full,
// ATTENTION_BATCH at a time. Rows whose check failed are not read in full:
// GitHub is slow or failing, and those are its heaviest requests. The work
// changes nothing but the kept inputs (`AttentionInputs.accept`): a run the
// store's runtime drops (superseded, or another account signed in) leaves
// the rows' attention as it was.
import { failureOf } from '../../api/failure';
import { RateLimitError } from '../../api/gh';
import { DASH_TIMELINE_ITEMS, type DashAttentionInputs, type DashPr } from '../../api/queries';
import { attentionBatch, type ServerOverride } from '../../api/server';
import type { AttentionInput, AttentionMember, AttentionResult as EngineResult } from '../../engine/types';
import type { AttentionGap } from '../../logic/attentionGap';
import { reviewSummary, type ReviewSummary } from '../../logic/reviewSummary';
import type { ReadTicket } from '../../runtime/cache';
import { botsIn, reviewerLogin, threadSummaries, toTimelineEvents } from '../../logic/timeline';
import { ATTENTION_BATCH, keptInputs, readBatch, type AttentionInputs, type KeptResult, type ReadOptions } from '../attentionInputs';

/** Rows per `/api/attention/batch` request for rows whose kept inputs hold (the server takes up to 100). */
const OVERRIDES_BATCH = 100;
/** How long rows a search hands over wait for others, so their kept inputs are checked in one request. */
export const CHECK_COALESCE_MS = 50;

/** What a run learned of one row. */
export interface RowAttention {
  /** Its attention set; null when it could not be worked out (`gap` says why). */
  members: AttentionMember[] | null;
  gap: AttentionGap | null;
  /**
   * Its reviewers and threads from the inputs read: null when the inputs
   * did not load (an earlier read's would colour the row as if current);
   * absent when the run read no inputs for it (its check failed).
   */
  review?: ReviewSummary | null;
}

/** What a run of the attention work learned. */
export interface AttentionResult {
  rows: Map<string, RowAttention>;
  /** A check of kept inputs failed other than on a rate limit: check them again later, with backoff. */
  checkAgain: boolean;
  /** Every check of kept inputs went through: the backoff starts over. */
  checksOk: boolean;
  /** A request met a rate limit: reload once it lifts, at this time at the latest it said (epoch ms); null when none did. */
  limitedUntil: number | null;
}

/** What the dashboard knows of its rows' attention, by node ID. */
export interface KnownAttention {
  readonly attention: ReadonlyMap<string, AttentionMember[]>;
  /** Why a row's attention could not be worked out (it stays unknown). */
  readonly errors: ReadonlyMap<string, AttentionGap>;
  /** Each row's reviewers and threads, from the inputs read with its attention. */
  readonly reviews: ReadonlyMap<string, ReviewSummary>;
  /**
   * The ticket each known attention set was worked out under: that of the
   * work that read the row and inputs it came from (taken as it began), or
   * the one a saved copy's was stored under. An answer is only as new as
   * that, however late it is shown or saved. None where nothing is saved.
   */
  readonly at: ReadonlyMap<string, ReadTicket>;
}

export const noAttention: KnownAttention = { attention: new Map(), errors: new Map(), reviews: new Map(), at: new Map() };

/** `known` with what `result` learned, by work that took `ticket` as it began reading (null: nothing is saved). */
export function applyAttention(known: KnownAttention, result: AttentionResult, ticket: ReadTicket | null): KnownAttention {
  if (!result.rows.size) return known;
  const attention = new Map(known.attention);
  const errors = new Map(known.errors);
  const reviews = new Map(known.reviews);
  const at = new Map(known.at);
  for (const [id, r] of result.rows) {
    if (r.members) {
      attention.set(id, r.members);
      if (ticket) at.set(id, ticket);
      else at.delete(id);
      errors.delete(id);
    } else {
      // Unknown, not "waiting on nobody".
      attention.delete(id);
      at.delete(id);
      if (r.gap) errors.set(id, r.gap);
    }
    if (r.review === null) reviews.delete(id);
    else if (r.review) reviews.set(id, r.review);
  }
  return { attention, errors, reviews, at };
}

/** `known` without what it holds of `ids`: their attention is unknown again, and their reviewers are not shown. */
export function forgetAttention(known: KnownAttention, ids: Iterable<string>): KnownAttention {
  const attention = new Map(known.attention);
  const errors = new Map(known.errors);
  const reviews = new Map(known.reviews);
  const at = new Map(known.at);
  for (const id of ids) {
    attention.delete(id);
    errors.delete(id);
    reviews.delete(id);
    at.delete(id);
  }
  return { attention, errors, reviews, at };
}

/**
 * Checks of kept inputs waiting to be sent together, by run: rows with
 * kept inputs wait CHECK_COALESCE_MS for the rows the run hands over next,
 * and are checked with them.
 */
export class CheckQueue {
  private queues = new Map<string, { prs: DashPr[]; done: Promise<KeptResult> }>();

  /** `keptInputs` for these rows, sent with the other rows `run` hands over within CHECK_COALESCE_MS. */
  async check(run: string, todo: DashPr[], kept: AttentionInputs, opts: ReadOptions): Promise<KeptResult> {
    if (opts.fresh || !(await kept.candidates(todo)).size) return keptInputs(todo, kept, opts);
    let q = this.queues.get(run);
    if (!q) {
      const prs: DashPr[] = [];
      const done = new Promise((r) => setTimeout(r, CHECK_COALESCE_MS)).then(() => {
        this.queues.delete(run);
        return keptInputs(prs, kept, opts);
      });
      q = { prs, done };
      this.queues.set(run, q);
    }
    q.prs.push(...todo);
    return q.done;
  }
}

/** What the attention work works with. */
export interface AttentionDeps {
  kept: AttentionInputs;
  /** A row's attention set, by the engine's rules. */
  engine: (input: AttentionInput) => Promise<EngineResult>;
  checks: CheckQueue;
}

export interface AttentionOptions {
  /** Use no kept inputs (Try again, Refresh). */
  fresh: boolean;
  /** The run the rows belong to: rows it hands over close together are checked together. */
  run: string;
  /** Aborted when the run is dropped: its waiting requests are not sent. */
  signal: AbortSignal;
}

const prKey = (p: DashPr) => `${p.repository.nameWithOwner}#${p.number}`.toLowerCase();

/** Work out the attention of `todo` (see the file comment). */
export async function workOutAttention(todo: DashPr[], deps: AttentionDeps, opts: AttentionOptions): Promise<AttentionResult> {
  const { kept } = deps;
  let limitedUntil: number | null = null;
  const onRateLimit = (e: RateLimitError) => (limitedUntil = Math.max(limitedUntil ?? -Infinity, e.limit.until));
  const read: ReadOptions = { fresh: opts.fresh, signal: opts.signal, onRateLimit };
  const { ticket, inputs: held, failed: unchecked } = await deps.checks.check(opts.run, todo, kept, read);
  const rows = new Map<string, RowAttention>();
  for (const p of todo) {
    const e = unchecked.get(p.id);
    if (e !== undefined) rows.set(p.id, { members: null, gap: { part: 'github', failure: failureOf(e) } });
  }
  const checkAgain = [...unchecked.values()].some((e) => !(e instanceof RateLimitError));
  const checksOk = !unchecked.size;
  const ready = todo.filter((p) => held.has(p.id));
  const misses = todo.filter((p) => !held.has(p.id) && !unchecked.has(p.id));
  const work: Promise<void>[] = [];
  const batch = (prs: DashPr[], reading: Promise<{ inputs: Map<string, DashAttentionInputs>; failed: Map<string, unknown> }>) =>
    work.push(attentionBatchOf(prs, deps, opts.signal, reading).then((got) => got.forEach((r, id) => rows.set(id, r))));
  for (let i = 0; i < ready.length; i += OVERRIDES_BATCH)
    batch(ready.slice(i, i + OVERRIDES_BATCH), Promise.resolve({ inputs: held, failed: new Map() }));
  for (let i = 0; i < misses.length; i += ATTENTION_BATCH) {
    const prs = misses.slice(i, i + ATTENTION_BATCH);
    batch(prs, readBatch(prs, kept, ticket, read));
  }
  await Promise.all(work);
  return { rows, checkAgain, checksOk, limitedUntil };
}

/** One batch: its inputs (as `reading` brings them) and overrides, then each row's set by the engine's rules. */
async function attentionBatchOf(
  todo: DashPr[],
  deps: AttentionDeps,
  signal: AbortSignal,
  reading: Promise<{ inputs: Map<string, DashAttentionInputs>; failed: Map<string, unknown> }>,
): Promise<Map<string, RowAttention>> {
  // Without the overrides the attention set is wrong, not just less detailed.
  let overrides: Map<string, ServerOverride[]> | { failed: unknown } = new Map();
  const [{ inputs, failed }] = await Promise.all([
    reading,
    attentionBatch(
      todo.map((p) => ({ owner: p.repository.owner.login, repo: p.repository.name, number: p.number })),
      { signal },
    ).then(
      (batch) => {
        overrides = new Map(batch.map((b) => [`${b.owner}/${b.repo}#${b.number}`.toLowerCase(), b.overrides]));
      },
      (e) => {
        overrides = { failed: e };
      },
    ),
  ]);
  const out = new Map<string, RowAttention>();
  await Promise.all(
    todo.map(async (p) => {
      const input = inputs.get(p.id);
      if (!input) {
        const e = failed.get(p.id);
        const gap: AttentionGap =
          e === undefined
            ? { part: 'missing', failure: failureOf(new Error('GitHub returned no data for this pull request')) }
            : { part: 'github', failure: failureOf(e) };
        out.set(p.id, { members: null, gap, review: null });
        return;
      }
      const review = reviewSummary(p, input);
      if (!(overrides instanceof Map)) {
        out.set(p.id, { members: null, gap: { part: 'overrides', failure: failureOf(overrides.failed) }, review });
        return;
      }
      try {
        const res = await deps.engine(engineInput(p, input, overrides.get(prKey(p)) ?? []));
        out.set(p.id, { members: res.members, gap: null, review });
      } catch (e) {
        out.set(p.id, { members: null, gap: { part: 'engine', failure: failureOf(e) }, review });
      }
    }),
  );
  return out;
}

/** The engine's input for row `p` from its inputs and overrides. */
function engineInput(p: DashPr, input: DashAttentionInputs, overrides: ServerOverride[]): AttentionInput {
  const author = p.author?.login ?? 'ghost';
  return {
    author,
    draft: p.isDraft,
    state: p.state,
    requested: p.reviewRequests.nodes.map((n) => reviewerLogin(n.requestedReviewer)).filter((x): x is string => !!x),
    events: toTimelineEvents(input.timelineItems.nodes, { author, createdAt: p.createdAt }),
    threads: threadSummaries(
      input.reviewThreads.nodes.map((t) => ({
        isResolved: t.isResolved,
        // The thread's first comments; the viewer's drafts are not published.
        comments: t.first.nodes.map((c) => ({
          author: c.author,
          pending: c.pullRequestReview?.state === 'PENDING',
        })),
      })),
    ),
    overrides: overrides.map((o) => ({ login: o.login, state: o.state, basis: o.basis, setAt: o.set_at })),
    bots: botsIn(input.timelineItems.nodes, [p.author]),
    // Inputs kept from before `pageInfo` was read: a full window may be cut off.
    truncated: input.timelineItems.pageInfo?.hasPreviousPage ?? input.timelineItems.nodes.length >= DASH_TIMELINE_ITEMS,
  };
}
